import React from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/system';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';

const sweep = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/**
 * Radar Scan Spinner
 * 레이더 스캔 스타일 로딩 스피너
 * 리스크 탐지 및 분석 프로세스 시각화에 최적
 */
export const RadarScanSpinner = ({ size = 200, message = '' }) => {
  const { colors, isDark } = useCustomTheme();
  
  const accentColor = colors.accent;
  const bgColor = colors.primaryBg;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: size,
          height: size,
          position: 'relative'
        }}
      >
        <svg
          width={size}
          height={size}
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            {/* Glow 효과 */}
            <filter id="radarGlow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>

            {/* 레이더 스윕 그라데이션 */}
            <radialGradient id="sweepGradient">
              <stop offset="0%" stopColor={accentColor} stopOpacity="0"/>
              <stop offset="50%" stopColor={accentColor} stopOpacity="0.6"/>
              <stop offset="100%" stopColor={accentColor} stopOpacity="0.9"/>
            </radialGradient>

            {/* 마스크 for 스윕 효과 */}
            <mask id="sweepMask">
              <rect width="200" height="200" fill="black"/>
              <g id="sweepBeam">
                <path
                  d="M 100 100 L 100 0 A 100 100 0 0 1 200 100 Z"
                  fill="url(#sweepGradient)"
                />
              </g>
            </mask>
          </defs>

          {/* 배경 */}
          <rect width="200" height="200" fill={bgColor}/>

          {/* 레이더 동심원들 */}
          {[20, 40, 60, 80, 90].map((radius, i) => (
            <circle
              key={i}
              cx="100"
              cy="100"
              r={radius}
              fill="none"
              stroke={accentColor}
              strokeWidth={i === 4 ? "2" : "1"}
              opacity={0.2 + i * 0.05}
            />
          ))}

          {/* 십자선 */}
          <g opacity="0.3">
            <line x1="100" y1="10" x2="100" y2="190" stroke={accentColor} strokeWidth="1"/>
            <line x1="10" y1="100" x2="190" y2="100" stroke={accentColor} strokeWidth="1"/>
          </g>

          {/* 대각선 */}
          <g opacity="0.2">
            <line x1="30" y1="30" x2="170" y2="170" stroke={accentColor} strokeWidth="0.5"/>
            <line x1="170" y1="30" x2="30" y2="170" stroke={accentColor} strokeWidth="0.5"/>
          </g>

          {/* 회전하는 스윕 빔 */}
          <g 
            mask="url(#sweepMask)"
            style={{
              transformOrigin: 'center',
              animation: `${sweep} 3s linear infinite`
            }}
          >
            <circle
              cx="100"
              cy="100"
              r="90"
              fill="none"
              stroke={accentColor}
              strokeWidth="30"
              opacity="0.15"
              filter="url(#radarGlow)"
            />
          </g>

          {/* 스윕 라인 */}
          <line
            x1="100"
            y1="100"
            x2="100"
            y2="10"
            stroke={accentColor}
            strokeWidth="2"
            filter="url(#radarGlow)"
            style={{
              transformOrigin: 'center',
              animation: `${sweep} 3s linear infinite`
            }}
          />

          {/* 랜덤 탐지 포인트들 - 리스크 신호 */}
          {[
            { x: 130, y: 70, delay: 0 },
            { x: 70, y: 130, delay: 0.5 },
            { x: 150, y: 120, delay: 1 },
            { x: 60, y: 80, delay: 1.5 },
            { x: 140, y: 140, delay: 2 },
            { x: 80, y: 60, delay: 2.5 }
          ].map((point, i) => (
            <g key={i}>
              {/* 탐지 포인트 */}
              <circle
                cx={point.x}
                cy={point.y}
                r="3"
                fill={accentColor}
                filter="url(#radarGlow)"
              >
                <animate
                  attributeName="opacity"
                  values="1;0.3;1"
                  dur="3s"
                  begin={`${point.delay}s`}
                  repeatCount="indefinite"
                />
              </circle>

              {/* 탐지 링 - 펄스 효과 */}
              <circle
                cx={point.x}
                cy={point.y}
                r="3"
                fill="none"
                stroke={accentColor}
                strokeWidth="1"
              >
                <animate
                  attributeName="r"
                  values="3;12;3"
                  dur="2s"
                  begin={`${point.delay}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.8;0;0.8"
                  dur="2s"
                  begin={`${point.delay}s`}
                  repeatCount="indefinite"
                />
              </circle>

              {/* 위험 마커 */}
              <path
                d={`M ${point.x} ${point.y - 8} L ${point.x + 4} ${point.y - 14} L ${point.x - 4} ${point.y - 14} Z`}
                fill="none"
                stroke={accentColor}
                strokeWidth="1"
                opacity="0.6"
              >
                <animate
                  attributeName="opacity"
                  values="0;0.8;0"
                  dur="3s"
                  begin={`${point.delay}s`}
                  repeatCount="indefinite"
                />
              </path>
            </g>
          ))}

          {/* 중앙 프로세싱 코어 */}
          <g>
            <circle
              cx="100"
              cy="100"
              r="8"
              fill={bgColor}
              stroke={accentColor}
              strokeWidth="2"
              filter="url(#radarGlow)"
            />
            
            <circle
              cx="100"
              cy="100"
              r="4"
              fill={accentColor}
              filter="url(#radarGlow)"
            >
              <animate
                attributeName="opacity"
                values="0.6;1;0.6"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </circle>
          </g>

          {/* 외부 테두리 */}
          <circle
            cx="100"
            cy="100"
            r="95"
            fill="none"
            stroke={accentColor}
            strokeWidth="1"
            opacity="0.4"
          />

          {/* 코너 눈금 */}
          {[0, 90, 180, 270].map((angle, i) => {
            const rad = (angle * Math.PI) / 180;
            const x1 = 100 + 90 * Math.cos(rad);
            const y1 = 100 + 90 * Math.sin(rad);
            const x2 = 100 + 95 * Math.cos(rad);
            const y2 = 100 + 95 * Math.sin(rad);
            
            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={accentColor}
                strokeWidth="2"
                opacity="0.6"
              />
            );
          })}

          {/* 스캔 파동 효과 */}
          <circle
            cx="100"
            cy="100"
            r="10"
            fill="none"
            stroke={accentColor}
            strokeWidth="2"
            opacity="0"
          >
            <animate
              attributeName="r"
              values="10;90"
              dur="4s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0.6;0"
              dur="4s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
      </Box>

      {/* 로딩 메시지 */}
      {message && (
        <Box
          sx={{
            color: colors.secondaryText,
            fontSize: '0.875rem',
            fontWeight: 500,
            textAlign: 'center'
          }}
        >
          {message}
        </Box>
      )}
    </Box>
  );
};

export default RadarScanSpinner;

